import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DynamicDialog, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CustomDialogData } from '../../../core/models/custom-dialog.model';
import { EmissionWithTitresDTO, PrimaireEmissionModel, TitresDTO } from '../../../core/models/emission.model';
import { EmissionService } from '../../../core/services/emission.service';
import { ToastService } from '../../../core/services/toast.service';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { take } from 'rxjs';
import { UEMOA_COUNTRIES } from '../../../core/models/country.model';
import { DividerModule } from 'primeng/divider';
import { CardModule } from 'primeng/card';

@Component({
    selector: 'app-emission-details',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ButtonModule,
        InputTextModule,
        InputNumberModule,
        SelectModule,
        DatePickerModule,
        DividerModule,
        CardModule
    ],
    templateUrl: './emission-details.component.html',
    styleUrl: './emission-details.component.scss'
})
export class EmissionDetailsComponent implements OnInit {
  private emissionService = inject(EmissionService);
  private toastService = inject(ToastService);
  private ref = inject(DynamicDialogRef);

  formGroup = new FormGroup({
    region: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    operationDate: new FormControl<Date | null>(null, { validators: [Validators.required] }),
    valueDate: new FormControl<Date | null>(null, { validators: [Validators.required] }),
    amount: new FormControl<number>(0, { nonNullable: true, validators: [Validators.required, Validators.min(0)] }),
    titresDTO: new FormArray<FormGroup>([])
  });

  boundData: CustomDialogData<EmissionWithTitresDTO> | undefined;
  instance: DynamicDialog | undefined;
  isCreate = false;
  isEdit = false;
  isView = false;

  countries = UEMOA_COUNTRIES;
  instrumentTypes = [
    { label: 'OAT (Obligation Assimilable du Trésor)', value: 'OAT' },
    { label: 'BAT (Bon Assimilable du Trésor)', value: 'BAT' }
  ];

  ngOnInit() {
    this.boundData = this.instance?.data;

    if (this.boundData?.isCreate) {
      this.isCreate = true;
      this.addTitre(); // Ajouter au moins un titre par défaut
    }

    if (this.boundData?.isEdit) {
      this.isEdit = true;
    }

    if (this.boundData?.isView) {
      this.isView = true;
      this.formGroup.disable();
    }

    if ((this.boundData?.isEdit || this.boundData?.isView) && this.boundData.data) {
      this.patchFormValues(this.boundData.data);
    }
  }

  get titresFormArray(): FormArray {
    return this.formGroup.get('titresDTO') as FormArray;
  }

  patchFormValues(emission: EmissionWithTitresDTO) {
    this.formGroup.patchValue({
      region: emission.region,
      operationDate: new Date(emission.operationDate),
      valueDate: new Date(emission.valueDate),
      amount: emission.amount
    });

    // Ajouter les titres existants
    if (emission.titresDTO && emission.titresDTO.length > 0) {
      emission.titresDTO.forEach(titre => {
        this.addTitre(titre);
      });
    }
  }

  createTitreFormGroup(titre?: TitresDTO): FormGroup {
    return new FormGroup({
      id: new FormControl(titre?.id || ''),
      isin: new FormControl(titre?.isin || ''),
      typeInstrument: new FormControl(titre?.typeInstrument || '', { validators: [Validators.required] }),
      dueDate: new FormControl<Date | null>(titre?.dueDate ? new Date(titre.dueDate) : null, { validators: [Validators.required] }),
      rate: new FormControl<number>(titre?.rate || 0, { validators: [Validators.required, Validators.min(0)] }),
      unitNominalValue: new FormControl<number>(titre?.unitNominalValue || 0, { validators: [Validators.required, Validators.min(0)] })
    });
  }

  addTitre(titre?: TitresDTO) {
    this.titresFormArray.push(this.createTitreFormGroup(titre));
  }

  removeTitre(index: number) {
    if (this.titresFormArray.length > 1) {
      this.titresFormArray.removeAt(index);
    } else {
      this.toastService.showToast('warn', 'Attention', 'Une émission doit contenir au moins un titre');
    }
  }

  save() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      this.toastService.showToast('warn', 'Attention', 'Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (this.titresFormArray.length === 0) {
      this.toastService.showToast('warn', 'Attention', 'Veuillez ajouter au moins un titre');
      return;
    }

    const formValue = this.formGroup.getRawValue();

    const emission: PrimaireEmissionModel = {
      region: formValue.region,
      operationDate: this.formatDate(formValue.operationDate!),
      valueDate: this.formatDate(formValue.valueDate!),
      amount: formValue.amount,
      titresDTO: formValue.titresDTO.map((titre: any) => ({
        id: titre.id || undefined,
        isin: titre.isin || undefined,
        typeInstrument: titre.typeInstrument,
        dueDate: this.formatDate(titre.dueDate),
        rate: titre.rate,
        unitNominalValue: titre.unitNominalValue
      }))
    };

    this.emissionService.createEmission(emission)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.toastService.showToast('success', 'Succès', 'Émission créée avec succès');
          this.ref.close();
        },
        error: (error) => {
          this.toastService.showToast('error', 'Erreur', 'Erreur lors de la création de l\'émission');
          console.error(error);
        }
      });
  }

  formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00:00`;
  }

  cancel() {
    this.ref.close();
  }
}
